"use client";

import type {
  NotificationDomain,
  PlatformChangeType,
} from "@/lib/notifications/platform-updates";

export const NOTIFICATIONS_UNREAD_COUNT_KEY = "notifications-unread-count";

/**
 * Predicate for `mutate(filter)` when `user_notifications` changes for this user.
 * Covers header unread count, inbox page + infinite caches, and dropdown SWR keys.
 */
export function shouldRevalidateNotificationCacheKey(
  key: unknown,
  userId: string,
): boolean {
  if (!userId || typeof key !== "string") return false;
  if (key === NOTIFICATIONS_UNREAD_COUNT_KEY) return true;
  if (key.includes(`notifications-dropdown:${userId}`)) return true;
  if (key.startsWith(`notifications-dropdown-unread:${userId}`)) return true;
  if (key.startsWith(`notifications-dropdown-unread-platform:${userId}`)) {
    return true;
  }
  if (key.includes("notifications-inbox")) return true;
  if (
    key === "notifications-inbox-unread:item" ||
    key === "notifications-inbox-unread:platform"
  ) {
    return true;
  }
  return false;
}

export type NotificationInboxItem = {
  id: string;
  event_type?: string | null;
  title: string;
  body: string;
  url: string | null;
  notification_domain: NotificationDomain;
  change_type: PlatformChangeType | null;
  mini_body: string | null;
  metadata?: unknown;
  cta_label: string | null;
  cta_url: string | null;
  read_at: string | null;
  created_at: string;
};

export type NotificationInboxPage = {
  items: NotificationInboxItem[];
  hasMore: boolean;
  unreadCount?: number;
};

export function getNotificationsInboxPageKey(
  offset: number,
  limit: number,
  notificationDomain?: NotificationDomain,
) {
  if (notificationDomain) {
    return `notifications-inbox:${notificationDomain}:${offset}:${limit}`;
  }
  return `notifications-inbox:${offset}:${limit}`;
}

export function getUnreadCountFromPages(
  pages: NotificationInboxPage[] | undefined,
): number {
  if (!pages || pages.length === 0) return 0;

  const serverUnreadCount = pages[0]?.unreadCount;
  if (typeof serverUnreadCount === "number") {
    return serverUnreadCount;
  }

  return pages.reduce(
    (total, page) => total + page.items.filter((item) => !item.read_at).length,
    0,
  );
}
